<template>
  <TooltipProvider>
    <TooltipRoot
      v-model:open="isOpen"
      :disabled
      :delayDuration="delayDuration"
      :disableClosingTrigger="isTouchScreen"
    >
      <TooltipTrigger asChild @pointerdown="open">
        <slot name="trigger" />
      </TooltipTrigger>

      <TooltipPortal>
        <Transition name="fade">
          <TooltipContent
            class="Tooltip"
            :side="positionByWidthScreen"
            :sideOffset="2"
            :collisionPadding="8"
          >
            <slot />

            <TooltipArrow class="Tooltip-arrow" />
          </TooltipContent>
        </Transition>
      </TooltipPortal>
    </TooltipRoot>
  </TooltipProvider>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipPortal,
} from 'reka-ui';
import { useResponsive } from '@cvo/ui/composables';
import { horizontalToVerticalPosition, type TooltipPosition, type TooltipProps } from './Tooltip.model';

const props = withDefaults(defineProps<TooltipProps>(), {
  position: 'top',
  delayDuration: 200,
});

const isTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const { isMobileOrTablet } = useResponsive();

const isOpen = ref();

const positionByWidthScreen = computed(() => {
  const isHorizontalPosition = props.position === 'right' || props.position === 'left';

  if (isMobileOrTablet.value && isHorizontalPosition) {
    return horizontalToVerticalPosition[props.position];
  }

  return props.position;
});

function open(event: TouchEvent) {
  isOpen.value = true;
  event.stopPropagation(); // Prevent bubbling
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .Tooltip {
    background-color: vars.$atoms-tag-tooltip-bg-color;
    color: vars.$atoms-tag-tooltip-text-color;
    border-radius: vars.$atoms-tag-tooltip-border-radius;
    border: vars.$atoms-tag-tooltip-border-stroke solid vars.$atoms-tag-tooltip-border-color;
    box-shadow: vars.$shadow-lg;
    word-wrap: break-word;
    min-width: 60px;

    @include responsive.mobile {
      padding: vars.$dimensions-d-2;
      font-size: vars.$font-size-xs;
      max-width: calc(100vw - vars.$dimensions-d-4);
    }

    .Tooltip-description {
      display: flex;
      gap: vars.$dimensions-d-3;
      @include text.text-responsive('xs', 'md');

      .Icon {
        color: vars.$atoms-tag-tooltip-icon-color;
        @include layout.square(16px);
      }
    }

    @include responsive.tablet {
      padding: vars.$dimensions-d-4;
      font-size: vars.$font-size-md;
      max-width: 420px;
    }

    &-arrow {
      fill: vars.$atoms-tag-tooltip-bg-color;
      width: 32px;
      height: 20px;

      // Fix a 1px gap between arrow and tooltip content on mobile
      .Tooltip[data-side=top],
      .Tooltip[data-side=bottom] {
        transform: translateY(-1px);
      }
    }

    // Direction modifiers
    &[data-side=right],
    &[data-side=left] {
      display: flex;
      align-items: center;
      min-height: 60px;
    }
  }
}
</style>
